"use client";

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../lib/hooks';
import { selectDate } from '../../lib/model/date/dateSlice.ts';
import {
    AanmeldingOptions,
    Periode,
    SignupPrefs,
    useAanmeldenMutation,
    useBatchAanmeldenMutation,
    useGetUserPreferencesQuery,
} from '../../lib/model/kantoordagApi';
import { useGetMyReservationsQuery } from '../../lib/model/kantoordag/kantoordagMyReservationsStreamingApi';
import { DateTime } from 'luxon';
import { QueryResult } from '../../components/Alerts.tsx';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import ProtectedRoute from "../../components/ProtectedRoute.tsx";
import { Card, ToggleSwitch } from "flowbite-react";
import { ROOM__AUTOMATIC_ASSIGNMENT, ROOM__NO_ROOM } from "../Constants.tsx";
import { FaBuilding, FaCar, FaQuestion, } from "react-icons/fa6";
import { FaEdit, FaParking } from "react-icons/fa";
import { GiOfficeChair } from "react-icons/gi";
import TodaysReservation from "../../components/TodaysReservation.tsx";
import { useBuildingContext } from "../../components/BuildingContext.tsx";
import { toast } from "react-toastify";
import { toastError } from "../../components/Toast.tsx";
import PreferenceEditorSlideIn from "../../components/PreferenceEditorSlideIn.tsx";
import { useReservationsTour } from './ReservationsTour.tsx'
import { Joyride } from "react-joyride";
import RequiresBuilding from "../../components/RequiresBuilding.tsx";
import './Reservations.css'; // Import the external CSS file
import OtherUserSignUp from "../../components/OtherUserSignup.tsx";
import ReservationDetailSlideIn, { ReservationDetail } from "../../components/ReservationDetailSlideIn.tsx";

export default function Reservations() {
    return (
        <RequiresBuilding>
            <ReservationsInternal/>
        </RequiresBuilding>
    )
}

function ReservationsInternal() {
    console.log("LOADING")
    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.user.user);
    const { t } = useTranslation("reservations");
    const [isEditableMode, setIsEditableMode] = useState(false);
    const [isPreferenceEditorOpen, setIsPreferenceEditorOpen] = useState(false);
    const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState<ReservationDetail | null>(null);

    const dateStr = useAppSelector(state => state.date.date);
    const date = useMemo(() => DateTime.fromISO(dateStr), [dateStr]);
    const currentMonth = useMemo(() => date.toJSDate(), [date]);
    const [email, setEmail] = useState<string>(user?.email ?? "")
    const fromDate = date.startOf('month').startOf('week').toISODate();
    const toDate = date.endOf('month').endOf('week').minus({ days: 2 }).toISODate();
    const todayIso = DateTime.now().toISODate();
    const selectedBuilding = useBuildingContext();

    // Queries and Mutations
    //
    // `email` is the signed-in user's own email by default, or another user's when an admin has
    // retargeted it via OtherUserSignUp. The Firestore security rule allows either the document
    // owner or a caller with the ADMIN role to read a given userId's MyReservations subcollection
    // (see specs/015-adopt-my-reservations-view/research.md Decision 3 for why that's safe: the
    // `groups` claim on the Firebase custom token comes from the caller's validated Entra role
    // assignment, not from anything the client controls), so one listener covers both cases.
    const calendarQuery = useGetMyReservationsQuery(
        { userId: email, fromDate: fromDate || '', toDate: toDate || '' },
        { skip: !email || !fromDate || !toDate }
    );
    const preferencesQuery = useGetUserPreferencesQuery();
    const [aanmelden, aanmeldenResult] = useAanmeldenMutation();
    const [batchAanmelden, batchAanmeldenResult] = useBatchAanmeldenMutation();

    // State for local preferences
    const [currentAanmeldingPreferences, setCurrentAanmeldingPreferences] = useState<SignupPrefs | undefined>(preferencesQuery.data?.signup);

    const reservationsByDate = useMemo(() => {
        const rows: ReservationDetail[] = (calendarQuery.data?.reservations ?? []).map((r): ReservationDetail => ({
            date: r.date,
            building: r.building,
            room: r.room,
            parkingRequested: r.requestedParkingspot,
            parkingAssigned: r.assignedParkingspot,
            periode: r.periode,
        }));

        return rows.reduce((acc, reservation) => {
            const date = DateTime.fromISO(reservation.date).toISODate();
            if (date) {
                if (!acc[date]) {
                    acc[date] = [];
                }
                acc[date].push(reservation);
            }
            return acc;
        }, {} as Record<string, ReservationDetail[]>);
    }, [calendarQuery.data]);

    const buildAanmeldingOptions = useCallback((signupPrefs: SignupPrefs) => {
        const options: AanmeldingOptions = {
            building: selectedBuilding?.name ?? "",
            room: signupPrefs.room === ROOM__AUTOMATIC_ASSIGNMENT ? ROOM__AUTOMATIC_ASSIGNMENT : signupPrefs.room,
            parkingRequested: signupPrefs.parkingRequested,
            periode: signupPrefs.periode ?? Periode.HeleDag,
        };
        return options;
    }, [selectedBuilding?.name]);

    const {
        runTour,
        tourSteps,
        handleJoyrideCallback,
        restartTour,
    } = useReservationsTour(setIsEditableMode, setIsPreferenceEditorOpen);

    // Effect to attach click listeners to day headers
    useEffect(() => {
        if (!isEditableMode) return;

        const calendarElement = document.querySelector('.react-calendar-custom');
        if (!calendarElement) return;

        const weekdayHeaders = calendarElement.querySelectorAll('.react-calendar__month-view__weekdays__weekday abbr');

        const handleWeekdayHeaderClick = async (event: Event) => {
            const target = event.target as HTMLElement;
            const dayIndex = Array.from(weekdayHeaders).indexOf(target); // 0 for Monday, 4 for Friday

            if (dayIndex === -1) return;

            const dayOfWeek = dayIndex + 1; // Luxon weekday: 1 for Monday, 7 for Sunday

            if (!currentAanmeldingPreferences) {
                toast.warn(t("no_preferences_set"));
                return;
            }

            const aanmeldingOptions = buildAanmeldingOptions(currentAanmeldingPreferences);

            const datesToReserve = determineDatesToReserve(dayOfWeek);

            if (datesToReserve.length > 0) {
                batchAanmelden({
                    batchAanmelding: {
                        userId: email,
                        data: datesToReserve,
                        options: aanmeldingOptions
                    }
                }).unwrap().then(() => {
                    toast.success(t("batch_reservation_success"));
                    calendarQuery.refetch();
                }).catch(() => {
                    toastError({ api: batchAanmeldenResult, message: t("batch_reservation_failed") });
                });
            } else {
                toast.info(t("no_days_to_batch_reserve"));
            }
        };

        weekdayHeaders.forEach(header => {
            header.addEventListener('click', handleWeekdayHeaderClick);
            header.classList.add('clickable-weekday-header'); // Add class for styling
        });

        return () => {
            weekdayHeaders.forEach(header => {
                header.removeEventListener('click', handleWeekdayHeaderClick);
                header.classList.remove('clickable-weekday-header'); // Clean up class
            });
        };

        function determineDatesToReserve(dayOfWeek: number) {
            const datesToReserve: string[] = [];
            // Start from the beginning of the current month
            let currentDateIterator = date.startOf('month');
            // End at the end of the current month
            const endOfCurrentMonth = date.endOf('month');

            while (currentDateIterator <= endOfCurrentMonth) {
                // Check if it's the correct day of the week, not in the past, and has no existing aanmelding
                if (currentDateIterator.weekday === dayOfWeek &&
                    currentDateIterator >= DateTime.now().startOf('day') &&
                    !reservationsByDate?.[currentDateIterator.toISODate() || '']?.length) {
                    datesToReserve.push(currentDateIterator.toISODate() || '');
                }
                currentDateIterator = currentDateIterator.plus({ days: 1 });
            }
            return datesToReserve;
        }
    }, [isEditableMode, date, email, currentAanmeldingPreferences, reservationsByDate, selectedBuilding, batchAanmelden,
        calendarQuery, t, batchAanmeldenResult, buildAanmeldingOptions]);


    const tileContent = ({ date, view }: { date: Date; view: string }) => {
        if (view === 'month') {
            const isoDate = DateTime.fromJSDate(date).toISODate();
            const dayReservations = reservationsByDate?.[isoDate || ''];

            return (
                <div className="flex flex-col items-center justify-center h-4 mt-1 overflow-hidden">
                    {dayReservations?.map((res, index) => (
                        <div key={index} className="flex items-center text-sm">
                            {res.room === ROOM__NO_ROOM && <FaBuilding className="mr-1 text-gray-600" title={`${t("reservation")}: ${res.building}`}/>}
                            {res.room !== ROOM__NO_ROOM && <GiOfficeChair className="mr-1 text-gray-600" title={`${t("room_reservation")}: ${res.room}`}/>}
                            {res.parkingRequested && !res.parkingAssigned && <FaCar className="text-gray-500" title={t("parking_reservation")}/>}
                            {res.parkingAssigned && <FaParking className="text-blue-500" title={t("parking_assigned")}/>}
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    const tileClassName = ({ date, view }: { date: Date; view: string }) => {
        if (view === 'month') {
            const classes = [];
            const isoDate = DateTime.fromJSDate(date).toISODate();
            const isPast = DateTime.fromJSDate(date) < DateTime.now().startOf('day');
            const dayReservations = reservationsByDate?.[isoDate || ''];
            if (dayReservations?.length) classes.push('has-reservation');
            if (isoDate === todayIso) classes.push('is-today');
            if (isPast) classes.push('is-past');
            return classes.length > 0 ? classes.join(' ') : null;
        }
        return null;
    };

    const onActiveStartDateChange = ({ activeStartDate }: { activeStartDate: Date | null }) => {
        if (activeStartDate) {
            dispatch(selectDate(DateTime.fromJSDate(activeStartDate)));
            setIsDetailViewOpen(false);
            setSelectedReservation(null);
        }
    };

    const handlePreferencesSave = (newPreferences: SignupPrefs) => {
        setCurrentAanmeldingPreferences(newPreferences);
        toast.success(t("local_preferences_updated"));
    };

    const handleDayClick = async (date: Date) => {
        const isoDate = DateTime.fromJSDate(date).toISODate();
        dispatch(selectDate(DateTime.fromJSDate(date)));
        const hasReservation = reservationsByDate?.[isoDate || '']?.length > 0;
        if (hasReservation) {
            setSelectedReservation(reservationsByDate?.[isoDate || '']?.[0])
            setIsPreferenceEditorOpen(false)
            setIsDetailViewOpen(true)
        } else {
            setSelectedReservation(null)
            setIsDetailViewOpen(false)
        }
        if (!isEditableMode) return;
        const isPast = DateTime.fromJSDate(date) < DateTime.now().startOf('day');

        if (isPast || hasReservation) return;

        if (!currentAanmeldingPreferences) {
            toast.warn(t("no_preferences_set"));
            return;
        }
        const aanmeldingOptions = buildAanmeldingOptions(currentAanmeldingPreferences)

        aanmelden({
            datum: isoDate || '',
            userId: email,
            aanmeldingOptions: aanmeldingOptions
        }).unwrap().then(() => {
            toast.success(t("reservation_success"));
            calendarQuery.refetch();
        }).catch(() => {
            toastError({ api: aanmeldenResult, message: t("reservation_failed") });
        })
    };

    const renderShorthandPreferences = () => {
        if (preferencesQuery.isLoading) return <span>{t("loading_preferences")}</span>;
        if (preferencesQuery.isError) return <span>{t("failed_to_load_preferences")}</span>;
        if (!currentAanmeldingPreferences) return <span>{t("no_preferences_set")}</span>;

        const signupPref = currentAanmeldingPreferences;
        const roomDisplay = signupPref.room === ROOM__NO_ROOM ? t("no_room") :
            signupPref.room === ROOM__AUTOMATIC_ASSIGNMENT ? t("automatic_room") :
                signupPref.room;
        const periodeDisplay = signupPref.periode === Periode.HeleDag ? t("whole_day") :
            (signupPref.periode === Periode.DagDeel ? t("part_day") : '');
        const hasNoPreferencesSet = !roomDisplay && !signupPref.parkingRequested && !periodeDisplay

        return (
            <div className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer" data-tut="preferences"
                 title={t("click_to_edit_local_preferences")}
                 onClick={() => {
                     setIsDetailViewOpen(false)
                     setIsPreferenceEditorOpen(true)
                 }}>
                <span><strong>{t("local_preferences")}:</strong></span>
                <span>{roomDisplay}</span>
                {roomDisplay && signupPref.parkingRequested && (<span>|</span>)}
                {signupPref.parkingRequested && (<span><FaCar/></span>)}
                {signupPref.parkingRequested && periodeDisplay && (<span>|</span>)}
                <span>{periodeDisplay}</span>
                {hasNoPreferencesSet && (<span>{t("no_preferences_set")}</span>)}
                <FaEdit className="ml-1"/>
            </div>
        );
    };

    return (
        <ProtectedRoute authenticated={true}>
            <div className="flex justify-between items-center mb-4">
                <h1>{t('my_reservations')}</h1>
            </div>
            <Joyride
                run={runTour}
                steps={tourSteps}
                continuous
                onEvent={handleJoyrideCallback}
                options={{
                    zIndex: 10000,
                    // Specify which buttons to display in the tooltip footer
                    buttons: ['back', 'skip', 'close', 'primary'],
                }}
            />
            <TodaysReservation userId={email} compact={true}/>
            <OtherUserSignUp email={email} setEmail={setEmail} signUpResult={batchAanmeldenResult}/>
            <div className="mb-5"/>
            <Card>
                <div className="flex justify-center items-center space-x-2">
                    <ToggleSwitch
                        checked={isEditableMode}
                        onChange={setIsEditableMode}
                        label={t('editable_mode')}
                        data-tut="edit-toggle"
                    />
                    <span className="text-sm text-gray-500">
                        {isEditableMode ? t('click_days_to_book') : t('view_only')}
                    </span>
                    <FaQuestion onClick={restartTour} className="flex justify-end"/>
                </div>
                {isEditableMode && (
                    <div className="flex justify-center">
                        {renderShorthandPreferences()}
                    </div>
                )}
                <QueryResult query={calendarQuery} message={t("loading_failed")}>
                    <div className="flex justify-center" data-testid={"calendar-container"}>
                            <span data-tut="calendar">
                            <Calendar
                                activeStartDate={currentMonth}
                                value={currentMonth}
                                tileContent={tileContent}
                                tileClassName={tileClassName}
                                onActiveStartDateChange={onActiveStartDateChange}
                                onClickDay={handleDayClick}
                                calendarType="iso8601"
                                className={`react-calendar-custom ${isEditableMode ? 'is-editable' : ''}`}
                            />
                            </span>
                    </div>
                </QueryResult>
            </Card>
            <PreferenceEditorSlideIn
                isOpen={isPreferenceEditorOpen}
                onClose={() => setIsPreferenceEditorOpen(false)}
                initialPreferences={currentAanmeldingPreferences}
                onSave={handlePreferencesSave}
            />
            <ReservationDetailSlideIn
                isOpen={isDetailViewOpen}
                onClose={() => setIsDetailViewOpen(false)}
                reservation={selectedReservation}
            />
        </ProtectedRoute>
    );
}